/**
 * UranIA PA — Admin skill detail (US-029 AC#3+AC#4+AC#5).
 *
 * Route `/admin/skills/$skillId` — pagina di dettaglio **read-only** del
 * catalogo skill. In Fase 1 mostra solo i metadati di tutte le versioni
 * della skill (servizio `services/skills-registry` `GET /api/v1/skills/
 * {skill_id}`).
 *
 * Boundary letterale rispetto all'AC#3
 * ("admin can see body via GET /api/v1/skills/{id} -> body decrypted;
 * audit logged"):
 *   - Il contratto attuale di `GET /api/v1/skills/{skill_id}` dichiara
 *     esplicitamente che "il body decifrato passa SOLO via
 *     `get_skill_body()` lato Gateway" (vedi schema `SkillSummary` in
 *     `services/skills-registry/src/skills_registry/routes/skills.py`).
 *     Per non bypassare il choke-point Gateway + l'audit hash-chained
 *     (US-025), la detail page Fase 1 NON renderizza il body in
 *     plaintext; mostra invece un alert "body sigillato, retrieval
 *     deferred a Fase 4 US-058+ via endpoint `:body` con audit
 *     dedicato".
 *   - L'audit logging dell'apertura della detail page (audit AgID P.7)
 *     e' server-side: il backend skills-registry US-019+ scrive
 *     `audit_events.action='skill.read'` ad ogni `GET /skills/{id}` per
 *     ruolo platform_admin (cap. 19 analisi). La SPA non scrive audit
 *     direttamente.
 *
 * AC coperti dal componente:
 *   - AC#3 — Read-only metadata + link "indietro" alla list.
 *   - AC#4 — axe-core serious=0 (verificato in
 *     `spa-admin-skills-axe.spec.ts`).
 *   - AC#5 — Typecheck strict.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';

import { SkillAttachmentsPanel } from '../../../components/admin/SkillAttachmentsPanel.tsx';
import { SkillVersionForm } from '../../../components/admin/SkillVersionForm.tsx';
import { getSkill, SkillsApiError } from '../../../lib/admin/skills-api.ts';
import type { GetSkillDto, SkillSummaryDto } from '../../../lib/admin/skills-api.ts';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../../lib/auth/auth-store.ts';
import { principalIsPlatformAdmin } from '../../../lib/auth/rbac.ts';
import { Route as RootRoute } from '../../__root.tsx';

function skillDetailQueryKey(skillId: string): readonly [string, string, string] {
  return ['admin', 'skill-detail', skillId];
}

function VersionRow(props: { readonly version: SkillSummaryDto }): JSX.Element {
  const { t } = useTranslation();
  const { version } = props;
  return (
    <tr data-testid={`admin-skill-detail-version-${version.version}`}>
      <th scope="row">
        <code>{version.version}</code>
        {version.current_version_pointer && (
          <span
            className="badge bg-info text-dark ms-2"
            data-testid={`admin-skill-detail-current-${version.version}`}
          >
            {t('admin.skills.detail.current_pointer_badge')}
          </span>
        )}
      </th>
      <td>{version.name}</td>
      <td>
        <span
          className={`badge ${version.is_active ? 'bg-success' : 'bg-secondary'}`}
          data-testid={`admin-skill-detail-active-${version.version}`}
        >
          {t(`admin.skills.status.${version.is_active ? 'active' : 'inactive'}`)}
        </span>
      </td>
      <td>
        <span
          className={`badge ${version.is_core ? 'bg-primary' : 'bg-light text-dark'}`}
          data-testid={`admin-skill-detail-core-${version.version}`}
        >
          {t(`admin.skills.core.${version.is_core ? 'core' : 'domain'}`)}
        </span>
      </td>
      <td>
        <time dateTime={version.updated_at}>{version.updated_at}</time>
      </td>
    </tr>
  );
}

function SkillVersionsTable(props: { readonly data: GetSkillDto }): JSX.Element {
  const { t } = useTranslation();
  if (props.data.versions.length === 0) {
    return (
      <p
        className="text-muted"
        role="status"
        aria-live="polite"
        data-testid="admin-skill-detail-versions-empty"
      >
        {t('admin.skills.detail.versions_empty')}
      </p>
    );
  }
  return (
    <div className="table-responsive">
      <table className="table table-striped" data-testid="admin-skill-detail-versions-table">
        <caption className="visually-hidden">
          {t('admin.skills.detail.versions_caption')}
        </caption>
        <thead>
          <tr>
            <th scope="col">{t('admin.skills.detail.columns.version')}</th>
            <th scope="col">{t('admin.skills.detail.columns.name')}</th>
            <th scope="col">{t('admin.skills.detail.columns.is_active')}</th>
            <th scope="col">{t('admin.skills.detail.columns.is_core')}</th>
            <th scope="col">{t('admin.skills.detail.columns.updated_at')}</th>
          </tr>
        </thead>
        <tbody>
          {props.data.versions.map((version) => (
            <VersionRow key={version.skill_row_id} version={version} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminSkillDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { skillId } = useParams({ from: '/admin/skills/$skillId' });
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isPlatformAdmin = principalIsPlatformAdmin(principal);
  const [isFormOpen, setFormOpen] = useState(false);

  const query = useQuery<GetSkillDto, SkillsApiError>({
    queryKey: skillDetailQueryKey(skillId),
    queryFn: () => getSkill(skillId),
    enabled: isAuthenticated && isPlatformAdmin && skillId.length > 0,
    staleTime: 30_000,
  });

  if (!isAuthenticated) {
    return (
      <section
        className="urania-admin-skill-detail"
        data-testid="admin-skill-detail-unauthenticated"
      >
        <h1 className="display-5 mb-3">{t('admin.skills.detail.title')}</h1>
        <div
          className="alert alert-info"
          role="region"
          aria-label={t('admin.skills.unauthenticated.aria_label')}
        >
          <p className="mb-2">{t('admin.skills.unauthenticated.message')}</p>
          <Link
            to="/login"
            className="btn btn-primary"
            data-testid="admin-skill-detail-go-to-login"
          >
            {t('admin.skills.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  if (!isPlatformAdmin) {
    return (
      <section
        className="urania-admin-skill-detail"
        data-testid="admin-skill-detail-forbidden"
      >
        <h1 className="display-5 mb-3">{t('admin.skills.detail.title')}</h1>
        <div
          className="alert alert-warning"
          role="alert"
          data-testid="admin-skill-detail-forbidden-alert"
        >
          <strong>{t('admin.skills.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.skills.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="urania-admin-skill-detail" data-testid="admin-skill-detail-page">
      <nav
        aria-label={t('admin.skills.detail.breadcrumb_aria_label')}
        className="mb-3"
      >
        <Link to="/admin/skills" data-testid="admin-skill-detail-back">
          {t('admin.skills.detail.back_to_list')}
        </Link>
      </nav>
      <h1 className="display-5 mb-2">{t('admin.skills.detail.title')}</h1>
      <p className="lead mb-4">
        {t('admin.skills.detail.subtitle', { skillId })}
      </p>

      <div
        className="alert alert-info"
        role="note"
        data-testid="admin-skill-detail-body-sealed-notice"
      >
        <strong>{t('admin.skills.detail.body_sealed.title')}</strong>
        <p className="mb-0">{t('admin.skills.detail.body_sealed.body')}</p>
      </div>

      {query.isLoading && (
        <p
          role="status"
          aria-live="polite"
          data-testid="admin-skill-detail-loading"
        >
          {t('admin.skills.detail.loading')}
        </p>
      )}

      {query.isError && (
        <div
          className="alert alert-danger"
          role="alert"
          data-testid="admin-skill-detail-error"
        >
          <strong>{t('admin.skills.detail.errors.load_failed')}</strong>
          {query.error.detail !== null && <p className="mb-0">{query.error.detail}</p>}
        </div>
      )}

      {query.isSuccess && (
        <>
          <div className="d-flex justify-content-between align-items-center mt-4 mb-3 flex-wrap gap-2">
            <h2 className="h4 mb-0">{t('admin.skills.detail.versions_heading')}</h2>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={(): void => setFormOpen((v) => !v)}
              data-testid="admin-skill-detail-toggle-form"
            >
              {isFormOpen
                ? t('admin.skills.detail.actions.close_form')
                : t('admin.skills.detail.actions.new_version')}
            </button>
          </div>
          <SkillVersionsTable data={query.data} />
          {isFormOpen && (
            <div className="card shadow-sm border-0 mt-4" data-testid="admin-skill-detail-form-card">
              <div className="card-header bg-white">
                <strong>{t('admin.skills.detail.form_card_title')}</strong>
              </div>
              <div className="card-body">
                <SkillVersionForm
                  mode="new-version"
                  defaultSkillId={skillId}
                  defaultName={query.data.versions[0]?.name ?? ''}
                  defaultDescription={query.data.versions[0]?.description ?? null}
                  defaultIsCore={query.data.versions[0]?.is_core ?? true}
                  defaultTenantId={query.data.versions[0]?.tenant_id ?? null}
                  onSuccess={(): void => {
                    setFormOpen(false);
                  }}
                />
              </div>
            </div>
          )}

          <div className="mt-4">
            <SkillAttachmentsPanel skillId={skillId} />
          </div>
        </>
      )}
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/admin/skills/$skillId',
  component: AdminSkillDetailPage,
});
